/**
 * Room -> MeshCore private channel.
 *
 * A room rides LoRa as an ordinary MeshCore channel whose 16-byte secret is
 * derived from the room secret. Anyone holding the room secret derives the
 * same channel, so the stock MeshCore app can join the room's text too, given
 * the secret (hex) and any channel name. Room text never goes on the public
 * channel.
 *
 * The channel name is stored on the radio only (it is not sent over the air)
 * and is deliberately non-identifying: "ct-" + 6 hex chars of a hash.
 */

import { CHANNEL_SECRET_LEN } from './companion'

const enc = new TextEncoder()

export interface RoomChannel {
  name: string
  secret: Uint8Array
  /** Hex of `secret`, for sharing with the stock MeshCore app. */
  secretHex: string
}

function toHex(b: Uint8Array): string {
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
}

export async function deriveRoomChannel(roomSecret: string, roomId: string): Promise<RoomChannel> {
  const base = await crypto.subtle.importKey('raw', enc.encode(roomSecret), 'HKDF', false, ['deriveBits'])
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: enc.encode(`crowdtalkie-meshcore-v1-${roomId}`),
      info: enc.encode('meshcore-channel-secret'),
    },
    base,
    CHANNEL_SECRET_LEN * 8
  )
  const secret = new Uint8Array(bits)
  const tag = new Uint8Array(await crypto.subtle.digest('SHA-256', secret)).subarray(0, 3)
  return { name: `ct-${toHex(tag)}`, secret, secretHex: toHex(secret) }
}
